import React, { useRef } from 'react';
import classNames from 'classnames';
import { connect } from 'react-redux';

import { useIntl } from '../../util/reactIntl';
import { pickCollectionListings } from '../../util/listingCollections';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import { selectListingCollection } from '../../ducks/listingCollections.duck';

import ListingCard from '../ListingCard/ListingCard';
import NamedLink from '../NamedLink/NamedLink';

import css from './ListingCollection.module.css';

const KEY_CODE_ARROW_LEFT = 37;
const KEY_CODE_ARROW_RIGHT = 39;

/**
 * A horizontally scrollable row of listing cards ("More from this seller", "Just listed", ...).
 *
 * Renders nothing when the collection has fewer listings than `minListings` (default 1), so a
 * collection that isn't set up yet - or is too thin to read as a row - simply doesn't appear.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to component's own css.root
 * @param {string?} props.rootClassName overwrite component's own css.root
 * @param {Object} props.collection collection config (id, title or titleKey, fallbackTitleKey,
 *   limit, footerLink)
 * @param {Array} props.listings listings to render
 * @param {Object?} props.titleValues values for the title translation (e.g. { authorName })
 * @param {string?} props.excludeListingId uuid that is filtered out of the results
 * @param {Array<string>?} props.excludeListingIds uuids shown by another collection on this page
 * @returns {JSX.Element|null}
 */
export const ListingCollectionComponent = props => {
  const {
    className,
    rootClassName,
    collection,
    listings = [],
    titleValues,
    excludeListingId,
    excludeListingIds,
  } = props;
  const intl = useIntl();
  const sliderRef = useRef(null);

  const visibleListings = pickCollectionListings(listings, {
    excludeListingId,
    excludeListingIds,
    limit: collection.limit,
  });

  // One or two cards read as a gap in the page rather than a short row.
  if (visibleListings.length < (collection.minListings || 1)) {
    return null;
  }

  // Fall back to a title without values when a value is missing (e.g. a listing with no author name)
  const hasAllTitleValues = Object.values(titleValues || {}).every(value => !!value);
  const titleKey =
    hasAllTitleValues || !collection.fallbackTitleKey
      ? collection.titleKey
      : collection.fallbackTitleKey;
  // A literal title (home page feeds) wins over a translation key.
  const title = collection.title || intl.formatMessage({ id: titleKey }, titleValues);
  const { footerLink } = collection;

  // Scroll a full row at a time; scroll-snap then aligns to the nearest card.
  const slideBy = direction => {
    const slider = sliderRef.current;
    if (!slider) {
      return;
    }
    slider.scrollLeft = slider.scrollLeft + direction * slider.clientWidth;
  };

  const onKeyDown = e => {
    if (e.keyCode === KEY_CODE_ARROW_LEFT) {
      e.preventDefault();
      slideBy(-1);
    } else if (e.keyCode === KEY_CODE_ARROW_RIGHT) {
      e.preventDefault();
      slideBy(1);
    }
  };

  return (
    <section
      className={classNames(rootClassName || css.root, className)}
      aria-labelledby={`${collection.id}-title`}
    >
      <header className={css.header}>
        <h2 className={css.title} id={`${collection.id}-title`}>
          {title}
        </h2>
        <div className={css.headerActions}>
          {footerLink ? (
            <NamedLink
              className={css.footerLink}
              name={footerLink.link.name}
              params={footerLink.link.params}
              to={footerLink.link.to}
            >
              {footerLink.label}
            </NamedLink>
          ) : null}
          <div className={css.arrows}>
            <button
              className={css.arrow}
              type="button"
              onClick={() => slideBy(-1)}
              onKeyDown={onKeyDown}
              aria-label={intl.formatMessage({ id: 'ListingCollection.previous' })}
            >
              ‹
            </button>
            <button
              className={css.arrow}
              type="button"
              onClick={() => slideBy(1)}
              onKeyDown={onKeyDown}
              aria-label={intl.formatMessage({ id: 'ListingCollection.next' })}
            >
              ›
            </button>
          </div>
        </div>
      </header>

      <div className={css.slider} ref={sliderRef}>
        {visibleListings.map(listing => (
          <ListingCard
            key={listing.id.uuid}
            className={css.card}
            listing={listing}
            renderSizes="(max-width: 767px) calc(50vw - 36px), (max-width: 1023px) 33vw, 260px"
            enableGallery={false}
          />
        ))}
      </div>
    </section>
  );
};

const mapStateToProps = (state, ownProps) => {
  const { listingIds } = selectListingCollection(state, ownProps.collection.id);
  return { listings: getListingsById(state, listingIds) };
};

const ListingCollection = connect(mapStateToProps)(ListingCollectionComponent);

export default ListingCollection;
